'use client';

import React, { useRef, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { StyledButton } from "../Buttons/StyledButton";
import TextInput from "../Forms/TextInput";
import SelectInput from "../Forms/SelectInput";
import RadioGroup from "../Forms/RadioGroup";
import TextareaInput from "../Forms/TextareaInput";
import apiClient from "../../api/apiClient";
import { submitContact } from "./submitContact";
import PhoneNumberInput from "../Forms/PhoneNumberInput";

const requestContact = (url, method, data) => apiClient.request({
  url, method, data, timeout: 30000, _skipAuthRefresh: true,
});

const ContactForm = ({ matchid, setMatchid }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [contactData] = useState({
    full_name: "",
    email: "",
    phone_number: "",
    service: matchid,
    preferred_contact: "1",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const submitting = useRef(false);

  const handleSubmit = async (values, { resetForm }) => {
    if (submitting.current) return;
    submitting.current = true;
    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");
    try {
      await submitContact(values, executeRecaptcha, requestContact);
      setSuccessMessage("Thank you. Your enquiry has been received by Centera Tech.");
      resetForm({ values: { ...contactData, service: values.service } });
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  return (
    <>
      <div className="relative h-full p-px rounded-2xl overflow-hidden group max-w-[900px]">
        {/* Rotating gradient background */}
        <div className="absolute inset-0 rounded-xl bg-[linear-gradient(155deg,var(--accent-light),#555_60%)] transition-transform duration-700 group-hover:rotate-180 blur-3xl scale-150"></div>
        <div className="relative py-8 md:py-10 px-4 sm:px-8 md:px-12 lg:px-20 rounded-2xl bg-color-98">
          <div className="flex flex-col items-start gap-8 self-stretch text-color-1 text-2xl lg:text-4xl font-semibold mb-5">
            Let’s Connect!
          </div>
          <FormComponent
            loading={loading}
            verificationReady={typeof executeRecaptcha === "function"}
            contactData={contactData}
            handleSubmit={handleSubmit}
            successMessage={successMessage}
            errorMessage={errorMessage}
            setSuccessMessage={setSuccessMessage}
            setMatchid={setMatchid}
          />
          <div className="absolute top-10 right-10 -rotate-45 w-[350px] h-[200px] bg-[radial-gradient(circle,_var(--accent-dark),_#0000)] blur-3xl opacity-30 pointer-events-none"></div>
        </div>
      </div>
    </>
  );
};

export default ContactForm;

const FormComponent = ({
  loading,
  verificationReady,
  contactData,
  handleSubmit,
  successMessage,
  errorMessage,
  setSuccessMessage,
  setMatchid,
}) => {
  const validationSchema = Yup.object().shape({
    full_name: Yup.string().required("Full Name is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    phone_number: Yup.string()
      .matches(/^\+\d{1,4}\s?\d{4,14}$/, "Enter a valid phone number")
      .required("Phone number is required"),
    service: Yup.string().required("Service Type is required"),
    preferred_contact: Yup.string().required(
      "Preferred Contact Method is required"
    ),
  });
  return (
    <Formik
      initialValues={contactData}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
      }) => (
        <Form className="flex flex-col gap-5 w-full" aria-busy={loading}>
          <fieldset disabled={loading} className="flex flex-col gap-5 min-w-0 border-0 p-0 m-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <TextInput
                label="Full Name*"
                id="full_name"
                name="full_name"
                value={values.full_name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.full_name && errors.full_name}
              />
              {errors.full_name && touched.full_name && (
                <div className="text-red-500 text-xs mt-1">
                  {errors.full_name}
                </div>
              )}
            </div>

            <div>
              <TextInput
                label="Email*"
                id="email"
                name="email"
                type="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.email && errors.email}
              />
              {errors.email && touched.email && (
                <div className="text-red-500 text-xs mt-1">{errors.email}</div>
              )}
            </div>

            <div>
              <PhoneNumberInput
                label="Mobile*"
                id="phone_number"
                name="phone_number"
                type="tel"
                inputMode="tel"
                value={values.phone_number}
                onChange={handleChange}
                setFieldValue={setFieldValue}
                onBlur={handleBlur}
                error={touched.phone_number && errors.phone_number}
              />

              {errors.phone_number && touched.phone_number && (
                <div className="text-red-500 text-xs mt-1">
                  {errors.phone_number}
                </div>
              )}
            </div>

            <div>
              <SelectInput
                label="Service Type*"
                id="service"
                name="service"
                value={values.service}
                onChange={(e) => {
                  setMatchid(e.target.value);
                  handleChange(e);
                }}
                onBlur={handleBlur}
                options={[
                  { value: 1, label: "Website Design & Development" },
                  { value: 2, label: "Mobile App Development" },
                  { value: 3, label: "Custom Software Development" },
                  { value: 4, label: "UI/UX Design" },
                  { value: 5, label: "Consulting Services" },
                  { value: 6, label: "Live Streaming Platform" },
                  { value: 7, label: "E-commerce Development" },
                  { value: 8, label: "Cloud Services" },
                  { value: 9, label: "Product Development" },
                  { value: 10, label: "AI & Machine Learning" },
                  { value: 11, label: "Graphic Design & Digital Marketing" },
                  { value: 12, label: "Game Development" },
                ]}
              />
              {errors.service && touched.service && (
                <div className="text-red-500 text-xs mt-1">
                  {errors.service}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center flex-wrap gap-3">
            <label
              htmlFor="preferred_contact"
              className="text-color-2 pointer-events-none text-sm"
            >
              Preferred Contact Method
            </label>
            <RadioGroup
              name="preferred_contact"
              options={[
                { value: "1", label: "Call" },
                { value: "2", label: "SMS" },
                { value: "3", label: "Email" },
              ]}
              defaultChecked={values.preferred_contact}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {errors.preferred_contact && touched.preferred_contact && (
              <div className="text-red-500 text-xs mt-1">
                {errors.preferred_contact}
              </div>
            )}
          </div>

          <div>
            <TextareaInput
              label="Message"
              id="message"
              name="message"
              value={values.message}
              onChange={handleChange}
              onBlur={handleBlur}
              rows={10}
              maxLength={2400}
            />
          </div>

          </fieldset>
          {errorMessage && (
            <div role="alert" className="bg-red-100 text-red-900 p-4 rounded-md">
              <p>{errorMessage}</p>
              <a href="mailto:support@centera.tech" className="underline focus-visible:outline-2 focus-visible:outline-offset-2">Email support@centera.tech</a>
            </div>
          )}
          {successMessage && (
            <div role="status" className="bg-green-100 text-green-800 p-4 rounded-md flex items-center justify-between">
              <span>{successMessage}</span>
              <button
                type="button"
                aria-label="Dismiss confirmation"
                className="text-red-500 font-bold"
                onClick={() => setSuccessMessage("")}
              >
                ✕
              </button>
            </div>
          )}

          <div>
            {!verificationReady && (
              <p role="status" className="mb-3 text-sm text-color-2">
                Loading verification. If this does not finish, refresh the page or{" "}
                <a className="underline" href="mailto:support@centera.tech">email support@centera.tech</a>.
              </p>
            )}
            <StyledButton
              text={loading ? "Submitting…" : verificationReady ? "Submit" : "Loading verification…"}
              type="submit"
              disabled={loading || !verificationReady}
              className="focus-visible:outline-2 focus-visible:outline-offset-4 disabled:cursor-wait disabled:opacity-70"
              loading={loading}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};
